(ns app.domain.application.shared-process
  (:require [tilakone.core :as tk]
            [tilakone.util :as tku]))


;;
;; Process:
;;

(defn filter-signals-by-key
  "Accepts a process and returns seq of [signal-id k-value] that are defined in
  current process state and have `k` defined. Signals are returned
  in the order they are defined in process states."
  [process k]
  (->> process
       tku/current-state
       ::tk/transitions
       (filter k)
       (map (juxt ::tk/on k))
       distinct))

(defn primary-signals
  "Accepts a process and returns seq of signal ID's that are defined in
  current process state and have :signal-type defined. Signals are returned
  in the order they are defined in process states."
  [process]
  (filter-signals-by-key process :signal-type))


(defn route-allowed?
  [transition-guard-results]
  (every? ::tk/allow? transition-guard-results))


(def permissions-guards
  "List of guards which only fail due to permission or similar errors, i.e. no action the user can take to
   make these guards disappear. Such guards will not be shown in help texts."
  #{:process.guard/required-permissions
    :process.guard/required-permissions-one-of
    :process.guard/is-super-admin?
    :process.guard/is-batch-run?
    :process.guard/constantly-false
    :process.guard/has-any-required-authority-roles?
    :process.guard/user-has-bond-permission?
    ;; if feature is not enabled, the action will not become permitted
    ;; no matter what the user does, so hide those buttons like we would with
    ;; a permission failure
    :process.guard/feature-enabled?
    :process.guard/feature-not-enabled?
    :process.guard/review-integration-id
    ;; these are routing guards used only to pick between alternate transitions for the same
    ;; signal (e.g. whether finishing a review should also move the workspace out of review),
    ;; they are complements of each other so exactly one of the pair always passes; showing
    ;; the failing one to the user is meaningless since there's no action they can take about it
    :process.guard/no-other-reviews-in-status
    :process.guard/other-reviews-in-status
    :process.guard/final-review-can-complete?
    :process.guard/final-review-cannot-complete?
    :process.guard/final-review-can-resume?
    :process.guard/final-review-cannot-resume?
    :process.guard/config-flag-enabled?
    :process.guard/config-flag-not-enabled?
    :process.guard/can-create-admin-package?
    :process.guard/group-has-authority-delegates?
    :process.guard/workspace-does-not-have-flag?
    :process.guard/workspace-has-flag?
    :process.guard/is-follow-up?
    :process.guard/renewal-workspace?})

(defn forbidden-guard? [{guard ::tk/guard}]
  (or (permissions-guards (first guard))
      (= (last guard) :guard.status/forbidden)))

(defn route-forbidden?
  "Route is forbidden if either a permissions guard fails or some of the failing guards has
  `:guard.status/forbidden` as the last parameter."
  [transition-guard-results]
  (->> transition-guard-results
       (remove ::tk/allow?)
       (filter forbidden-guard?)
       seq))


(defn failing-non-permission-guards
  "Returns sequence of tuples, where tuple is [guard-id guard-result]."
  [result]
  (->> result
       (remove ::tk/allow?)
       (remove #(-> % ::tk/guard first permissions-guards))
       (map (juxt (comp first ::tk/guard) ::tk/result))
       seq))


(defn results->status-with-error [results]
  (cond
    ; No routes available at all => forbidden:
    (empty? results) [:forbidden nil]

    ; Some route available => allow:
    (some route-allowed? results) [:allowed nil]

    ;; All routes forbidden => forbidden:
    (every? route-forbidden? results) [:forbidden nil]

    ; Some route was forbidden by other than permissions => disabled:
    :else [:disabled (mapcat failing-non-permission-guards results)]))

(defn signal-results [process signal-id]
  (->> (tk/apply-guards process [signal-id])
       (map second)))

(defn signal-allowed-status [process signal-id]
  (->> (signal-results process signal-id)
       results->status-with-error))

(defn- failure-count [route]
  (count (remove ::tk/allow? route)))

(defn select-route-with-least-errors
  "Results is a seq of 'guard results' per transition.
  There might be several possible transitions per signal.
  Format:
    [[result1-of-transition1, result2-of-transition1]
     [result1-of-transition2, result2-of-transition2]..

  Each result is a map with keys:
    `:tilakone.core/allow?` - boolean if guard passes (true) or fails (false)
    `:tilakone.core/guard` - the guard data from fsm
    `:tilakone.core/result` - the return value of guard"
  [transitions-results]
  (reduce (fn [acc route]
            (if (> (failure-count acc) (failure-count route))
              route
              acc))
          (first transitions-results)
          (rest transitions-results)))

(defn visible-errors-for-results
  "Given the per-route guard results for a signal (as returned by `signal-results`, i.e. a seq
  of routes, each route being a seq of individual guard-result maps), returns nil if the signal
  is fully allowed or fully forbidden (nothing actionable to tell the user about), otherwise the
  errors of a single route for which:
  1) Route is not allowed
  2) Route is not forbidden (these routes are considered 'hidden' from the user point of view)
  3) Route has the least errors of those routes that are not forbidden/allowed

  Output of this fn is a sequence of tuples, where tuple is [guard-name tk-result]."
  [results]
  (let [[status _] (results->status-with-error results)]
    (when (= status :disabled)
      (->> results
           (map #(remove ::tk/allow? %)) ; we are not interested in allowed guards
           (filter seq) ; keep only non-empty routes (eg routes with some errors)
           (remove route-forbidden?) ; remove forbidden rules (they are considered 'hidden')
           ;; this is hax to bypass getting unrelated errors from "not optimal"
           ;; route. Example case is: submit, where it can either go
           ;; to :submitted or :waiting-for-deposit states
           select-route-with-least-errors
           distinct
           (map (juxt (comp first ::tk/guard) ::tk/result))
           seq))))

(defn visible-errors-for-signal
  "See `visible-errors-for-results`."
  [process signal-id]
  (visible-errors-for-results (signal-results process signal-id)))
